if (!customElements.get('video-section')) {
  customElements.define(
    'video-section',
    class VideoSection extends HTMLElement {
      constructor() {
        super();

        this.parent = this.closest('.section-video');
        this.bgImage = this.parent.querySelector('.video__background');
        this.trigger = this.parent.querySelector('.video-controls');
        this.content = this.parent.querySelector('.content--wrapper');
        this.mp4 = this.querySelector('video');

        const observer = new IntersectionObserver(
          (entries, _observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.init();
                observer.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px 100% 0px' }
        );

        observer.observe(this);

        if (this.trigger) {
          this.trigger.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggleVideo(e.currentTarget.dataset.status, this.dataset.type);
          });
        }
      }

      init() {
        switch (this.dataset.type) {
          case 'youtube':
            this.initYoutubeVideo();
            break;

          case 'vimeo':
            this.initVimeoVideo();
            break;

          case 'mp4':
            this.initMp4Video();
            break;
        }
      }

      initYoutubeVideo() {
        this.setAsLoading();
        this.loadScript('youtube').then(this.setupYoutubePlayer.bind(this));
      }

      initVimeoVideo() {
        this.setAsLoading();
        this.loadScript('vimeo').then(this.setupVimeoPlayer.bind(this));
      }

      initMp4Video() {
        if (this.mp4) {
          const promise = this.mp4.play();

          // Edge does not return a promise (video still plays)
          if (typeof promise !== 'undefined') {
            const self = this;
            promise
              .then(function () {
                // playback normal - update icon to show pause
                if (self.trigger && self.mp4 && !self.mp4.paused) {
                  self.trigger.dataset.status = 'playing';
                }
              })
              .catch(function () {
                self.mp4.setAttribute('controls', '');
              });
          } else {
            // If video auto-plays (no promise), update icon
            if (this.trigger && this.mp4 && !this.mp4.paused) {
              this.trigger.dataset.status = 'playing';
            }
          }

          // Update icon when video play/pause state changes
          this.mp4.addEventListener('play', () => {
            if (this.trigger) {
              this.trigger.dataset.status = 'playing';
            }
          });

          this.mp4.addEventListener('pause', () => {
            if (this.trigger) {
              this.trigger.dataset.status = 'paused';
            }
          });
        }

        // pause when out of view
        const observer = new IntersectionObserver(
          (entries, _observer) => {
            entries.forEach((entry) => {
              entry.isIntersecting && this.trigger.dataset.status !== 'paused' ? this.mp4.play() : this.mp4.pause();
            });
          },
          { rootMargin: '0px 0px 50px 0px' }
        );

        observer.observe(this.mp4);
      }

      loadScript(videoType) {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script');
          document.body.appendChild(script);
          script.onload = resolve;
          script.onerror = reject;
          script.async = true;
          script.src = videoType === 'youtube' ? '//www.youtube.com/iframe_api' : '//player.vimeo.com/api/player.js';
        });
      }

      setAsLoading() {
        if (this.parent) {
          this.parent.setAttribute('loading', true);
        }
      }

      setAsLoaded() {
        if (this.parent) {
          this.parent.removeAttribute('loading');
          this.parent.setAttribute('loaded', true);
        }
      }

      toggleVideo(status, source) {
        if (status === 'start') {
          if (source === 'youtube') {
            this.player.seekTo(0);
            this.player.unMute();
          } else if (source === 'vimeo') {
            this.player.setCurrentTime(0);
            this.player.setVolume(1);
          } else {
            this.mp4.currentTime = 0;
            this.mp4.muted = !this.mp4.muted;
          }
          if (this.content) this.content.classList.add('visually-hidden');
          if (this.bgImage) this.bgImage.classList.remove('video__background');
          this.trigger.dataset.status = 'playing';
        } else if (status === 'paused') {
          if (source === 'youtube') {
            this.player.playVideo();
          } else if (source === 'vimeo') {
            this.vimeoPlay();
          } else {
            this.mp4 && this.mp4.play();
          }

          this.trigger.dataset.status = 'playing';
        } else {
          if (source === 'youtube') {
            this.player.pauseVideo();
          } else if (source === 'vimeo') {
            this.vimeoPause();
          } else {
            this.mp4 && this.mp4.pause();
          }

          this.trigger.dataset.status = 'paused';
        }
      }

      setupYoutubePlayer() {
        const videoId = this.dataset.videoId;

        const playerInterval = setInterval(() => {
          if (window.YT) {
            window.YT.ready(() => {
              const element = document.createElement('div');
              this.appendChild(element);

              this.player = new YT.Player(element, {
                videoId: videoId,
                playerVars: {
                  showinfo: 0,
                  controls: this.dataset.controls,
                  fs: this.dataset.controls,
                  rel: 0,
                  height: '100%',
                  width: '100%',
                  iv_load_policy: 3,
                  html5: 1,
                  loop: 1,
                  playsinline: 1,
                  modestbranding: 1,
                  disablekb: 1,
                },
                events: {
                  onReady: this.onYoutubeReady.bind(this),
                  onStateChange: this.onYoutubeStateChange.bind(this),
                },
              });
              clearInterval(playerInterval);
            });
          }
        }, 50);
      }

      onYoutubeReady() {
        this.iframe = this.querySelector('iframe'); // iframe once YT loads
        this.iframe && this.iframe.setAttribute('tabindex', '-1');

        if (typeof this.player.mute === 'function') this.player.mute();
        if (typeof this.player.playVideo === 'function') this.player.playVideo();

        // Update icon to show pause when video starts playing
        if (this.trigger) {
          this.trigger.dataset.status = 'playing';
        }

        this.setAsLoaded();

        // pause when out of view
        const observer = new IntersectionObserver(
          (entries, _observer) => {
            entries.forEach((entry) => {
              entry.isIntersecting && this.trigger.dataset.status !== 'paused'
                ? this.youtubePlay()
                : this.youtubePause();
            });
          },
          { rootMargin: '0px 0px 50px 0px' }
        );

        observer.observe(this.iframe);
      }

      onYoutubeStateChange(event) {
        switch (event.data) {
          case -1: // unstarted
            // Handle low power state on iOS by checking if
            // video is reset to unplayed after attempting to buffer
            if (this.attemptedToPlay) {
              this.setAsLoaded();
            }
            break;
          case 0: // ended, loop it
            this.youtubePlay();
            break;
          case 1: // playing
            this.setAsLoaded();
            // Update icon to show pause when playing
            if (this.trigger) {
              this.trigger.dataset.status = 'playing';
            }
            break;
          case 2: // paused
            // Update icon to show play when paused
            if (this.trigger) {
              this.trigger.dataset.status = 'paused';
            }
            break;
          case 3: // buffering
            this.attemptedToPlay = true;
            break;
        }
      }

      youtubePlay() {
        if (this.player && typeof this.player.playVideo === 'function') {
          this.player.playVideo();
        }
      }

      youtubePause() {
        if (this.player && typeof this.player.pauseVideo === 'function') {
          this.player.pauseVideo();
        }
      }

      setupVimeoPlayer() {
        const videoId = this.dataset.videoId;

        const playerInterval = setInterval(() => {
          if (window.Vimeo) {
            this.player = new Vimeo.Player(this, {
              id: videoId,
              autoplay: true,
              autopause: false,
              controls: true,
              loop: true,
              height: '100%',
              width: '100%',
            });
            this.player.ready().then(this.onVimeoReady.bind(this));

            clearInterval(playerInterval);
          }
        }, 50);
      }

      onVimeoReady() {
        this.iframe = this.querySelector('iframe');
        this.iframe.setAttribute('tabindex', '-1');

        this.player.setMuted(true);

        // Update icon to show pause when video starts playing
        if (this.trigger) {
          this.trigger.dataset.status = 'playing';
        }

        // Listen to play/pause events to update icon
        this.player.on('play', () => {
          if (this.trigger) {
            this.trigger.dataset.status = 'playing';
          }
        });

        this.player.on('pause', () => {
          if (this.trigger) {
            this.trigger.dataset.status = 'paused';
          }
        });

        this.setAsLoaded();

        // pause when out of view
        const observer = new IntersectionObserver(
          (entries, _observer) => {
            entries.forEach((entry) => {
              entry.isIntersecting && this.trigger.dataset.status !== 'paused' ? this.vimeoPlay() : this.vimeoPause();
            });
          },
          { rootMargin: '0px 0px 50px 0px' }
        );

        observer.observe(this.iframe);
      }

      vimeoPlay() {
        if (this.player && typeof this.player.play === 'function') {
          this.player.play();
        }
      }

      vimeoPause() {
        if (this.player && typeof this.player.pause === 'function') {
          this.player.pause();
        }
      }
    }
  );
}
